/**
 * The permissions catalogue, page by page (owner, 3 Oct 2026 - the approved plan, phase 1: "كل صفحة لوحدها بصلاحيتها" /
 * "كل الازرار فى كل الصفحات تخضع لنظام الصلاحيات"): every page has three levels - opening it, each tab, each button -
 * and every button a KIND (edit & save, delete, export & download, write on Ejar, run & pull, send, admin).
 *
 * NEW_KEYS are the permissions the plan adds. Each says what decides that button TODAY (`today`), so:
 *   - upgradeRole() gives a role every new permission equal to what it can do today (no one is locked out the day the
 *     buttons start asking for them - phases 2-4), and
 *   - until a user's profile carries CATALOG_VERSION, the app falls back to that same rule (usePermissions).
 * `today`: { any: [keys] } - one of them; { all: [keys] } - all of them; 'admin' - the admin only (nobody else).
 *
 * Only NEW permissions are given this way. A permission that already decides a button is never widened to more
 * people (review 3 Oct 2026: widening export_traffic_fines, add_service_record ... gave the Viewer role writes it
 * does not have): a later phase that puts a button under a permission gives that button a NEW key with its own
 * today's rule.
 */

export const CATALOG_VERSION = 2;

// the kinds of a button, in the order the editor shows them (words in English: the product's language)
export const KINDS = {
    open: { label: 'Open the page', tone: 'neutral' },
    tab: { label: 'Tabs', tone: 'neutral' },
    see: { label: 'See', tone: 'neutral' },
    edit: { label: 'Edit & save', tone: 'info' },
    delete: { label: 'Delete', tone: 'danger' },
    export: { label: 'Export & download', tone: 'success' },
    ejar: { label: 'Write on Ejar', tone: 'warning' },
    run: { label: 'Run & pull', tone: 'accent' },
    send: { label: 'Send (WhatsApp / e-mail)', tone: 'info' },
    admin: { label: 'Admin', tone: 'danger' },
};
export const KIND_ORDER = ['open', 'tab', 'see', 'edit', 'export', 'run', 'send', 'ejar', 'delete', 'admin'];

// what each shortcut of a page ticks: the kinds it holds
export const PRESETS = {
    view: { label: 'View only', kinds: ['open', 'tab', 'see'] },
    staff: { label: 'Staff', kinds: ['open', 'tab', 'see', 'edit', 'export', 'run', 'send', 'ejar'] },
    full: { label: 'Full', kinds: KIND_ORDER },
};

// -- the new permissions of the plan: [key, label, kind, today] ----------------------------------------------------
const any = (...k) => ({ any: k });
const all = (...k) => ({ all: k });
const N = (page, rows) => rows.map(([key, label, kind, today]) => ({ key, label, kind, today, page }));

export const NEW_KEYS_LIST = [
    ...N('engine', [
        ['view_engine_customer', 'Customer tab', 'tab', any('view_engine')],
        ['view_engine_parking', 'Parking tab', 'tab', any('view_engine')],
        ['view_engine_salik', 'Salik tab', 'tab', any('view_engine')],
        ['engine_register_parking', 'Register parking on Ejar', 'ejar', any('control_engine')],
        ['engine_register_salik', 'Register Salik / Darb lines on Ejar', 'ejar', any('control_engine')],
        ['engine_mark_rows', 'Mark rows "on Ejar" / "not on Ejar"', 'edit', any('control_engine')],
        // the buttons ask control_engine today (phase 2 inventory, 4 Oct 2026): view_engine alone never had them
        ['engine_export_reports', 'Salik & parking vs Ejar reports, registration report', 'export', any('control_engine')],
        ['engine_health_fix', '"Fix now" in the engine check', 'run', any('control_engine')],
        ['engine_delete_log', 'Delete a row of the registration log', 'delete', 'admin'],
    ]),
    ...N('traffic_fines', [
        ['fines_check_ejar', 'Check Ejar now', 'run', all('export_traffic_fines', 'run_traffic_fines')],
        ['fines_set_paid_by', 'Set who paid (company / customer)', 'edit', any('run_traffic_fines')],
    ]),
    ...N('contracts', [
        ['view_contracts_tab_booking_expired', 'Booking Expired tab', 'tab', any('view_contracts_tab_all')],
        ['view_contracts_tab_late_payment', 'Late Payment tab', 'tab', any('view_contracts_tab_all')],
        ['view_contracts_tab_stale_km', 'Stale KM tab', 'tab', any('view_contracts_tab_all')],
        ['view_contracts_tab_no_rent', 'No Rent tab', 'tab', any('view_contracts_tab_all')],
        ['view_contracts_tab_unpaid', 'Unpaid tab', 'tab', any('view_contracts_tab_all')],
        ['contracts_fill_from_ejar', 'Fill from Ejar (every contract)', 'run', any('edit_monthly_leasing')],
        ['contracts_send_whatsapp', 'WhatsApp: payment invoice, Switch Back request', 'send', any('view_contracts')],
        // the buttons show with edit_monthly_ops and their handler asked manage_users (phase 3 inventory, 4 Oct 2026)
        ['monthly_edit_comments', 'Edit and delete comments', 'edit', all('manage_users', 'edit_monthly_ops')],
        ['export_contracts_cache', 'Export cache (maintenance and fleet inside)', 'export', any('export_contracts')],
    ]),
    ...N('monthly_calculator', [
        ['open_monthly_calculator', 'Open the calculator', 'see', any('view_contracts')],
        ['export_monthly_calculator', 'Download the calculator PDF', 'export', any('view_contracts')],
    ]),
    ...N('fleet', [
        ['fleet_bulk_remarks', 'Change the status or company of many cars at once', 'edit', any('view_fleet_list')],
        ['fleet_edit_vehicle', 'Edit a car', 'admin', 'admin'],
        ['fleet_download_license', 'Download and print the licence', 'export', any('view_fleet_list')],
        ['fleet_ejar_transfer', 'Move the car on Ejar', 'ejar', any('write_fleet')],
    ]),
    ...N('fleet_service_history', [
        ['view_service_analytics_tab', 'Reports & Analytics tab', 'tab', any('view_service_history')],
        ['service_adjust_limits', 'Adjust Limits (Upcoming)', 'edit', any('view_service_history')],
        ['export_service_history', 'Export service and inventory CSV, print', 'export', any('view_service_history')],
        ['service_repair_refs', 'Repair Ref Numbers', 'run', any('manage_roles')],
    ]),
    // Collections and Refunds live inside the Reminder tool: no view_ops_reminder, no way to them today (review 3 Oct
    // 2026 - a role with the portal but not the Reminder must not gain them)
    ...N('operations', [
        ['ops_collections_view', 'Collections tab (day 13)', 'tab', all('view_operations', 'view_ops_reminder')],
        ['ops_collections_rebuild', 'Rebuild from Ejar, Retry, Refresh', 'run', all('view_operations', 'view_ops_reminder')],
        ['ops_collections_export', 'Excel, Copy + Email', 'export', all('view_operations', 'view_ops_reminder')],
        ['ops_collections_mark', 'Mark collected', 'edit', all('control_engine', 'view_ops_reminder')],
        ['ops_refunds_mark', 'Confirm a refund', 'edit', all('ops_refunds', 'view_ops_reminder')],
        // Brokers (was "Rentway"): the booking sites' Dubai reservations (Rentway + Comfy) against their Ejar bookings
        // (owner, 23 Oct 2026); nobody but the admin until a role is given it
        ['view_ops_rentway', 'Brokers page (Rentway + Comfy vs Ejar, Read now, Excel)', 'tab', 'admin'],
        // phase 2 (owner, 23 Oct 2026): "Book on Ejar" / "Cancel on Ejar" on a reservation (writes on Ejar + the
        // branch e-mails)
        ['ops_rentway_book', 'Brokers: Book / Cancel on Ejar + branch e-mails', 'run', 'admin'],
        // Availability (owner, 24 Oct 2026): the airport's cars free per model day by day, the bookings with no car and
        // the booking sites' limits - a page of its own; nobody but the admin until a role is given it
        ['view_ops_availability', 'Availability page (airport cars and bookings, with customer names and contracts)', 'tab', 'admin'],
    ]),
    ...N('dashboard', [
        ['dashboard_resolve_alerts', 'Resolved / Reopen an alert', 'edit', any('view_fleet_list', 'write_fleet')],
        // the Retry is on the KPI Overview tab (not the Dashboard), Sync now on Database, Refresh on the airport page (phase 4)
        ['run_ejar_sync', 'Run the Ejar sync (Retry, Sync now, Refresh)', 'run', any('view_kpi_ops', 'view_database', 'view_airport_dashboard')],
    ]),
    ...N('kpi', [
        ['view_kpi_overview', 'Overview tab', 'tab', any('view_kpi_ops')],
        ['kpi_rebuild', 'Refresh that rebuilds the figures', 'run', any('view_kpi_dashboard', 'view_dashboard')],
        ['export_kpi', 'Every KPI export (staff, revenue, expenses, details)', 'export', any('view_kpi_dashboard')],
        ['kpi_edit_pricing', 'KPI settings: prices', 'edit', any('manage_kpi_settings')],
        ['kpi_edit_staff_names', 'KPI settings: staff names', 'edit', any('manage_kpi_settings')],
    ]),
    ...N('invoices', [
        ['manage_invoice_companies', 'Companies: add / edit / delete', 'edit', any('view_invoices')],
    ]),
    ...N('daily_booking', [
        ['add_manual_booking', 'Add a manual booking (sends Telegram)', 'edit', any('view_daily_booking')],
        ['update_manual_booking', 'Manual booking: Picked up / Cancel', 'edit', any('view_daily_booking')],
    ]),
    ...N('automation', [
        // the Ejar booking tab: a booking made ON Ejar from Yelo (owner, 21 Oct 2026); nobody but the admin until a
        // role is given it
        ['create_ejar_booking', 'Ejar booking tab (makes the booking on Ejar)', 'ejar', 'admin'],
    ]),
    ...N('custodies', [
        ['export_custody', 'Export the custody Excel', 'export', any('view_custodies')],
    ]),
    ...N('government_transactions', [
        ['gov_generate_documents', 'PDF, template, Excel upload', 'export', any('view_government_transactions')],
        ['gov_sales_invoice', 'Sales invoice: PDF and invoice number', 'edit', any('view_government_transactions')],
    ]),
    ...N('branch_charges', [
        ['export_branch_charges', 'Excel, copy for e-mail', 'export', any('view_branch_charges')],
        ['branch_charges_invoices', 'Invygo invoices (gives invoice numbers)', 'edit', any('view_branch_charges')],
    ]),
    ...N('history', [
        ['export_history', 'Export the archive CSV (names and phones inside)', 'export', any('view_history')],
        ['history_tab_multicar', 'Multi-car tab', 'tab', any('analyze_contracts_multicar')],
        // history_view_all_users (every user's activity) removed 21 Oct 2026: the activity log is the Admin role's own
        // (Administration > Activity log), no permission opens it
    ]),
    ...N('matcher', [
        ['export_car_usage', 'Car Usage: Excel (names and phones)', 'export', any('view_matcher')],
    ]),
    ...N('admin', [
        ['users_edit', 'Add and edit a user', 'admin', 'admin'],
        ['users_lock', 'Lock a user', 'admin', 'admin'],
        ['users_delete', 'Delete a user', 'delete', 'admin'],
        ['users_password_help', 'Password link and temporary password', 'admin', 'admin'],
        ['settings_global_refresh', 'Global Refresh', 'admin', 'admin'],
        ['settings_company', 'Company settings', 'admin', 'admin'],
        ['customers_delete_on_ejar', 'Delete customers tab (removes them on Ejar)', 'delete', 'admin'],
        ['manage_staff_on_ejar', 'Staff on Ejar tab', 'admin', 'admin'],
        ['view_api_reference', 'API reference tab', 'admin', 'admin'],
        ['roles_edit', 'Edit and save the roles', 'admin', 'admin'],
        ['schedule_reset', 'Back to server defaults (Schedules)', 'admin', any('manage_engine_schedule')],
    ]),
];

// permissions no button uses any more (the 3 Oct 2026 audit): they drop off every role
export const DEAD_KEYS = [
    'booking_delete_contracts', 'booking_migration_tool', 'booking_refresh_cars', 'booking_refresh_cloud', 'booking_reset_data',
    'booking_run_check', 'booking_save_cloud',
    'matcher_action_export', 'matcher_action_session', 'matcher_parking_view_invygo', 'matcher_parking_view_yelo',
    'matcher_salik_summary', 'matcher_salik_view_invygo', 'matcher_salik_view_yelo', 'matcher_source_fleet',
    'matcher_tab_airport', 'matcher_tab_contracts', 'matcher_tab_parking', 'matcher_tab_repeated', 'matcher_tab_salik',
    'matcher_tab_traffic', 'matcher_tab_unrented', 'matcher_traffic_export', 'matcher_traffic_toggle_payment',
    'matcher_traffic_view_all',
];

export const NEW_KEYS = Object.fromEntries(NEW_KEYS_LIST.map((k) => [k.key, k]));

// The Admin role's own powers - never given to another role (owner, 4 Oct 2026: "للـ Admin بس"): whoever manages users
// or roles can make themselves Admin, and the server keeps the rest to the Admin anyway. The editor shows them locked
// ("Admin only"), the shortcuts and "Grant all" skip them, and no list makes anyone else hold them.
export const ADMIN_LOCKED = new Set([
    'users_edit', 'users_lock', 'users_delete', 'users_password_help', 'roles_edit', 'settings_global_refresh',
    'settings_company', 'customers_delete_on_ejar', 'manage_staff_on_ejar', 'view_api_reference', 'engine_delete_log',
]);

/** Does a permission list satisfy a `today` rule? */
export const meetsToday = (today, held) => {
    if (!today || today === 'admin') return false;
    const has = (k) => held.has(k);
    if (today.all) return today.all.every(has);
    return (today.any || []).some(has);
};

/**
 * A role's permissions upgraded to the catalogue: every new permission whose today's rule the role meets is added; the
 * dead ones are dropped. -> { permissions, added: [keys], dropped: [keys] }.
 */
export function upgradeRole(permissions) {
    const held = new Set(permissions || []);
    const added = [];
    for (const { key, today } of NEW_KEYS_LIST) {
        if (!held.has(key) && meetsToday(today, held)) added.push(key);
    }
    const dead = new Set(DEAD_KEYS);
    const dropped = [...held].filter((k) => dead.has(k));
    const out = [...held].filter((k) => !dead.has(k)).concat(added);
    return { permissions: out, added, dropped };
}

/**
 * The app's check before a user's profile is upgraded (profile.permissionsVersion < CATALOG_VERSION): a NEW permission
 * follows its today's rule, so no button that asks for it locks out someone who could press it yesterday.
 */
export const allowedBeforeUpgrade = (key, permissions) => {
    const rule = NEW_KEYS[key]?.today;
    return rule ? meetsToday(rule, new Set(permissions || [])) : false;
};
